import Slider from '../lib/Slider.js';

export default {
    dependencies: ['$element', 'searchmanager', 'trackingservice'],
    callable(ele, searchManager, trackingService) {
        const inputEles = ele.querySelectorAll("input[type='number']");
        const outputEle = ele.querySelector('[data-output]');
        const startInputEle = inputEles[0];
        const endInputEle = inputEles[1];
        const query = searchManager.currentQuery;

        const opts = {};

        // eslint-disable-next-line no-cond-assign
        for (let n = 0, input; (input = inputEles[n++]); ) {
            if (query[input.name]) {
                input.value = query[input.name][0]; // multidict, thus first.
            }

            // These numbers are found by empirical testing.
            let roundingIntervals;
            let conversionConstant;
            let inverseConversionConstant;
            let conversionFunctionStartValue;

            switch (input.getAttribute('data-id')) {
                case 'bapForSalePriceSliderId':
                    roundingIntervals = [
                        [1000, 10],
                        [15000, 100],
                        [Infinity, 1000],
                    ];
                    conversionConstant = 0.3;
                    inverseConversionConstant = 10 / 3;
                    conversionFunctionStartValue = 0;

                    opts.distribution = getSliderDistributionWith(
                        roundingIntervals,
                        conversionFunctionStartValue,
                        conversionConstant,
                        inverseConversionConstant
                    );
                    break;
                case 'carUsedYearModelSliderId':
                    roundingIntervals = [[1990, 5], [Infinity, 1]];
                    conversionConstant = 2.2;
                    inverseConversionConstant = 5 / 11;

                    // Looking at the conversion-function with x-value 50 and above gives a more suitable
                    // distribution of values on the slider.
                    conversionFunctionStartValue = 50;

                    opts.distribution = getSliderDistributionWith(
                        roundingIntervals,
                        conversionFunctionStartValue,
                        conversionConstant,
                        inverseConversionConstant
                    );
                    break;
            }
        }

        opts.container = ele.querySelector('.finn-slider');
        if (startInputEle.hasAttribute('data-format-number')) {
            opts.formatter = function(value) {
                // http://stackoverflow.com/a/2901298
                return String(value).replace(
                    /\B(?=(\d{3})+(?!\d))/g,
                    '$&\u00A0'
                );
            };
        }

        // Initialize slider
        const slider = new Slider(startInputEle, endInputEle || null, opts);

        // FIXME: To be able to reference it from other controller. This is ugly.
        ele.slider = slider;

        markIfTouched();
        updateAriaLabel();

        slider.addEventListener('change', markIfTouched);
        slider.addEventListener('change', trackSliderChange);
        slider.addEventListener('change', updateAriaLabel);

        let startPreviousValue = startInputEle.value;
        let endPreviousValue = endInputEle.value;

        function trackSliderChange() {
            trackIfChanged(startPreviousValue, startInputEle);
            startPreviousValue = startInputEle.value;

            trackIfChanged(endPreviousValue, endInputEle);
            endPreviousValue = endInputEle.value;

            function trackIfChanged(previousValue, inputEle) {
                if (inputEle.value !== previousValue) {
                    trackingService.trackEventObject({
                        analyticsName: `navigation.filter-by-${inputEle.name.replace(
                            /_/g,
                            ''
                        )}.click`,
                        numberValue: inputEle.value,
                    });
                }
            }
        }

        function markIfTouched() {
            const isTouched =
                startInputEle.value !== '' ||
                (endInputEle && endInputEle.value !== '');
            const headingEle = ele.previousElementSibling;
            const trackEle = ele.querySelector('.finn-slider-track-inner');
            if (headingEle) {
                headingEle.classList.toggle('pea', isTouched);
            }

            if (trackEle) {
                trackEle.classList.toggle('bg-pea', isTouched);
            }
        }

        function updateAriaLabel() {
            const label = slider.getOutputValue(
                slider.startValue,
                slider.endValue
            );
            outputEle.setAttribute('aria-label', label.replace('–', 'til'));
        }

        // roundingIntervals :            Array structure: [intervalUpToLimit, intervalStepValue].
        //                                Decides how values are rounded when you slide a thumb on the slider.
        // conversionFunctionStartValue : If the slider has a minimum value higher than zero, this can be adjusted for so the
        //                                mathematical non-linear conversion-function (that distributes the values within the
        //                                slider) starts at x-value zero. Adjusting a slider slider with minimum value 2000 by 2000 gives
        //                                you the y-value for x-value 0 (0=4000-2000).
        // conversionConstant :           Tweeking this contant will give you different distribution of values within the slider.
        // inverseConversionConstant :    Number is used to calculate the inverse of the fromBaseToConverted-function.
        // eslint-disable-next-line max-len
        function getSliderDistributionWith(
            roundingIntervals,
            conversionFunctionStartValue,
            conversionConstant,
            inverseConversionConstant
        ) {
            function getIntervalStepFor(baseValueRounded, limitCheckCallback) {
                const validIntervals = roundingIntervals.filter(interval => {
                    const upToLimit = interval[0];
                    return limitCheckCallback(baseValueRounded, upToLimit);
                });

                const firstInterval = validIntervals[0];
                const defaultStep = 100;
                return firstInterval == null ? defaultStep : firstInterval[1];
            }

            function roundToClosestIntervalStep(
                baseValueRounded,
                intervalStep
            ) {
                const valueHitIntervalStep =
                    baseValueRounded % intervalStep === 0;

                if (valueHitIntervalStep) {
                    return baseValueRounded;
                } else {
                    const roundedFactor = Math.round(
                        baseValueRounded / intervalStep
                    );
                    return roundedFactor * intervalStep;
                }
            }

            const distribution = {
                fromBaseToConverted(value, minValue) {
                    if (Math.abs(value) === Infinity) {
                        return value;
                    } else if (value < 0) {
                        return 0;
                    } else {
                        const adjustingValue = Math.max(
                            minValue - conversionFunctionStartValue,
                            minValue
                        );
                        value -= adjustingValue;
                        return Math.pow(value, conversionConstant);
                    }
                },
                fromConvertedToBase(value, minValue) {
                    // The inverse function of the fromBaseToConverted-function.
                    if (Math.abs(value) === Infinity) {
                        return value;
                    } else if (value === 0) {
                        // Zero to be able to convert values back to base value.
                        return 0;
                    } else {
                        const baseValueWithoutAdjustment = Math.pow(
                            value,
                            inverseConversionConstant
                        );
                        const adjustingValue = Math.max(
                            minValue - conversionFunctionStartValue,
                            minValue
                        );
                        return baseValueWithoutAdjustment + adjustingValue;
                    }
                },
                roundBaseValue(baseValue) {
                    if (Math.abs(baseValue) === Infinity) {
                        return baseValue;
                    }
                    const baseValueRounded = Math.round(baseValue);
                    const intervalStep = getIntervalStepFor(
                        baseValueRounded,
                        (baseValueRounded, upToLimit) =>
                            baseValueRounded < upToLimit
                    );
                    return roundToClosestIntervalStep(
                        baseValueRounded,
                        intervalStep
                    );
                },
                nextBaseValue(baseValue, nextSteps) {
                    if (Math.abs(baseValue) === Infinity) {
                        return baseValue;
                    }
                    let newRoundedBaseValue = Math.round(baseValue);

                    for (let iStep = 1; iStep <= nextSteps; iStep++) {
                        const intervalStep = getIntervalStepFor(
                            newRoundedBaseValue,
                            // eslint-disable-next-line no-loop-func
                            (baseValueRounded, upToLimit) =>
                                newRoundedBaseValue < upToLimit
                        );
                        newRoundedBaseValue += intervalStep;
                    }
                    return newRoundedBaseValue;
                },
                previousBaseValue(baseValue, previousSteps) {
                    if (Math.abs(baseValue) === Infinity) {
                        return baseValue;
                    }
                    let newRoundedBaseValue = Math.round(baseValue);

                    for (let iStep = 1; iStep <= previousSteps; iStep++) {
                        const intervalStep = getIntervalStepFor(
                            newRoundedBaseValue,
                            // eslint-disable-next-line no-loop-func
                            (baseValueRounded, upToLimit) =>
                                newRoundedBaseValue <= upToLimit
                        );
                        newRoundedBaseValue -= intervalStep;
                    }
                    return newRoundedBaseValue;
                },
            };
            return distribution;
        }
    },
};



// WEBPACK FOOTER //
// ./controllers/slider.js